import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { QrCode } from "lucide-react";

function utcDayKey(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function formatDateLong(d = new Date()) {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDurationMinutes(mins) {
  const n = Math.max(0, Math.floor(Number(mins) || 0));
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

/** Closed absences + live open away (if any). */
function accumulatedLunchMinutes(pass, now) {
  if (!pass) return 0;
  let total = 0;
  for (const absence of pass.absences || []) {
    const d = Number(absence.duration_minutes);
    if (Number.isFinite(d) && d > 0) total += d;
  }
  if (pass.is_away) {
    const startIso = pass.temporary_absence_started_at || pass.away_at;
    if (startIso) {
      try {
        const secs = (now.getTime() - new Date(startIso).getTime()) / 1000;
        if (secs > 0) total += Math.floor(secs / 60);
      } catch {
        /* ignore bad timestamps */
      }
    }
  }
  return total;
}

/** Wall-clock minutes from check-in to now/checkout, minus lunch away. */
function accumulatedOnSiteMinutes(pass, now, lunchMinutes) {
  if (!pass?.check_in_at) return 0;
  try {
    const start = new Date(pass.check_in_at).getTime();
    const end = pass.check_out_at
      ? new Date(pass.check_out_at).getTime()
      : now.getTime();
    const gross = Math.max(0, Math.floor((end - start) / 60000));
    return Math.max(0, gross - Math.max(0, lunchMinutes || 0));
  } catch {
    return 0;
  }
}

/**
 * Full-screen Daily Check-In Pass — designed for superintendent glance (&lt;2s).
 */
export default function DailyCheckInPass({
  pass,
  onCheckOut,
  checkoutBusy = false,
  showCheckOut = false,
  onAway,
  onReturn,
  onResume,
  canStartAway = false,
  canReturn = false,
  canResume = false,
  actionBusy = false,
  footer = null,
}) {
  const [now, setNow] = useState(() => new Date());
  const [confirmCheckout, setConfirmCheckout] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setConfirmCheckout(false);
  }, [pass?.pass_id, pass?.status, pass?.check_out_at]);

  if (!pass) return null;

  const todayKey = utcDayKey(now);
  const passDay = pass.pass_day_key || "";
  const validToday = !passDay || passDay === todayKey;
  const checkedIn = pass.status === "checked_in" || pass.status === "open";
  const checkedOut = pass.status === "checked_out" || pass.status === "closed";
  const isAway = Boolean(pass.is_away);
  const needsResume = Boolean(pass.needs_resume || pass.grace_expired);
  const lunchMinutes = accumulatedLunchMinutes(pass, now);
  const onSiteMinutes = accumulatedOnSiteMinutes(pass, now, lunchMinutes);

  let badgeLabel = "Not valid";
  let badgeClass = "bg-destructive/15 text-destructive border-destructive/30";
  let dotClass = "bg-destructive";
  let shellClass = "border-destructive/30 from-destructive/10";

  if (checkedIn && validToday) {
    badgeLabel = "CHECKED IN";
    badgeClass = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40";
    dotClass = "bg-emerald-500 animate-pulse";
    shellClass = "border-emerald-500/50 from-emerald-500/15";
  } else if (checkedOut) {
    badgeLabel = "CHECKED OUT";
    badgeClass = "bg-muted text-muted-foreground border-border";
    dotClass = "bg-muted-foreground";
    shellClass = "border-border from-muted/40";
  }

  return (
    <div
      className={`flex-1 flex flex-col w-full max-w-md mx-auto rounded-2xl border-2 bg-gradient-to-b to-background px-5 py-6 ${shellClass}`}
      data-testid="attendance-daily-pass"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-primary mb-5">
        <QrCode size={18} aria-hidden="true" />
        <span className="font-heading font-semibold text-xs tracking-wide uppercase">
          Smart Attendance
        </span>
      </div>

      <div className="flex items-center gap-5 mb-6 w-full flex-wrap">
        <div
          className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 shrink-0 ${badgeClass}`}
          data-testid="attendance-pass-badge"
        >
          <span className={`inline-block h-3 w-3 rounded-full ${dotClass}`} aria-hidden="true" />
          <span className="font-heading font-bold text-base tracking-wide">{badgeLabel}</span>
        </div>
        <div className="min-w-0" data-testid="attendance-pass-id-block">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide leading-none mb-0.5">
            Pass ID
          </p>
          <p
            className="font-mono text-base tracking-widest font-semibold"
            data-testid="attendance-pass-id"
          >
            {pass.pass_id || "—"}
          </p>
        </div>
      </div>      {checkedIn && isAway && (
        <p className="text-sm text-muted-foreground -mt-4 mb-4" data-testid="attendance-pass-away-hint">
          {needsResume
            ? "Grace period ended — tap Resume to continue this check-in."
            : `Temporarily away · ${pass.grace_remaining_minutes ?? "—"} min left in grace`}
        </p>
      )}

      <div className="space-y-1 mb-6">
        <p
          className="text-3xl font-heading font-bold leading-tight"
          data-testid="attendance-pass-name"
        >
          {pass.worker_name || "—"}
        </p>
        <p className="text-lg text-foreground/90" data-testid="attendance-pass-person-type">
          {pass.person_type_label || pass.person_type || "—"}
        </p>
        {pass.company_name && (
          <p className="text-base text-muted-foreground" data-testid="attendance-pass-company">
            {pass.company_name}
          </p>
        )}
      </div>

      <div className="space-y-4 text-base flex-1">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Project / Site</p>
          <p className="font-semibold text-lg" data-testid="attendance-pass-site">
            {pass.site_name || "—"}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Checked in</p>
            <p className="font-medium tabular-nums" data-testid="attendance-pass-check-in">
              {formatTime(pass.check_in_at)}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              Accumulated lunch
            </p>
            <p className="font-medium tabular-nums" data-testid="attendance-pass-lunch-total">
              {formatDurationMinutes(lunchMinutes)}
              {isAway ? (
                <span className="text-muted-foreground text-sm font-normal"> · away</span>
              ) : null}
            </p>
          </div>
        </div>
        {checkedOut && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Checked out</p>
            <p className="font-medium tabular-nums" data-testid="attendance-pass-check-out">
              {formatTime(pass.check_out_at)}
            </p>
          </div>
        )}
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Time on site</p>
          <p className="font-medium tabular-nums" data-testid="attendance-pass-onsite-total">
            {formatDurationMinutes(onSiteMinutes)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Today</p>
          <p className="font-medium" data-testid="attendance-pass-day">
            {formatDateLong(now)}
            {passDay && passDay !== todayKey ? (
              <span className="text-destructive ml-2 text-sm">(pass day {passDay})</span>
            ) : null}
          </p>
        </div>
        <div
          className="rounded-lg border border-border/70 bg-background/90 px-3 py-3 text-center"
          data-testid="attendance-pass-live-clock"
        >
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Live clock</p>
          <p className="font-mono text-lg tabular-nums font-semibold">{now.toLocaleTimeString()}</p>
        </div>
      </div>

      {showCheckOut && checkedIn && (
        <div className="mt-6 flex flex-col gap-3">
          {(canStartAway || canReturn || canResume) && (
            <div className="flex flex-col gap-3">
              {canStartAway && (
                <Button
                  className="w-full text-lg min-h-12 touch-manipulation"
                  variant="secondary"
                  disabled={actionBusy || checkoutBusy || confirmCheckout}
                  onClick={onAway}
                  data-testid="attendance-pass-away"
                >
                  {actionBusy ? "Working…" : "Away for lunch"}
                </Button>
              )}
              {canReturn && (
                <Button
                  className="w-full text-lg min-h-12 touch-manipulation"
                  disabled={actionBusy || checkoutBusy || confirmCheckout}
                  onClick={onReturn}
                  data-testid="attendance-pass-return"
                >
                  {actionBusy ? "Working…" : "I'm back"}
                </Button>
              )}
              {canResume && (
                <Button
                  className="w-full text-lg min-h-14 touch-manipulation btn-glow"
                  disabled={actionBusy || checkoutBusy || confirmCheckout}
                  onClick={onResume}
                  data-testid="attendance-pass-resume"
                >
                  {actionBusy ? "Working…" : "Resume check-in"}
                </Button>
              )}
            </div>
          )}
          <div className="pt-5 mt-2 border-t border-border/60">
            {!confirmCheckout ? (
              <Button
                className="w-full text-lg min-h-14 touch-manipulation"
                variant="outline"
                disabled={checkoutBusy || actionBusy}
                onClick={() => setConfirmCheckout(true)}
                data-testid="attendance-pass-checkout"
              >
                Check Out
              </Button>
            ) : (
              <div
                className="rounded-lg border border-border bg-muted/40 px-3 py-3 space-y-3"
                data-testid="attendance-pass-checkout-confirm"
              >
                <p className="text-sm text-center text-foreground">
                  End this check-in and close your Daily Pass?
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    className="w-full text-lg min-h-14 touch-manipulation"
                    disabled={checkoutBusy || actionBusy}
                    onClick={onCheckOut}
                    data-testid="attendance-pass-checkout-confirm-yes"
                  >
                    {checkoutBusy ? "Checking out…" : "Yes, check out"}
                  </Button>
                  <Button
                    className="w-full text-base min-h-12 touch-manipulation"
                    variant="ghost"
                    disabled={checkoutBusy}
                    onClick={() => setConfirmCheckout(false)}
                    data-testid="attendance-pass-checkout-confirm-no"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {footer}
    </div>
  );
}
